import React from 'react';
import { useRoutes, Navigate, Outlet } from 'react-router-dom';
import { styled } from 'twin.macro';

import { createLocalizedRoutes, createLocalizedRoutesWithChildren } from '../../helpers/route-helper';
import { useAuth } from '../../hooks/use-auth';
import { useSite } from '../../hooks/use-site';
import { getLocalizedPath } from '../../helpers/localization-helper';
import UsersLoginPageComponent from '../users/login';
import QuestionnaireOnboardingPageComponent from '../questionnaire/onboarding';
import QuestionnaireAnswerPageComponent from '../questionnaire/answer';
import PaymentsCheckoutPageComponent from '../payments/checkout';
import PaymentsSuccessPageComponent from '../payments/success';
import PaymentsCancelPageComponent from '../payments/cancel';
import ProfileDashboardPageComponent from '../profile/dashboard';
import ProfileResultsPageComponent from '../profile/results';
import TextPageComponent from '../text/page';
import ModalPrivacyPolicy from '../../components/modal/privacy-policy';
import ModalTermsConditions from '../../components/modal/terms-conditions';
import ResponsiveUtility from '../../components/common/utilities/responsive';
import { parameters } from '../../localization/strings';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import ProfileBloodRegistrationPageComponent from '../profile/blood-registration';
import LayoutHeadingComponent from '../../components/layout/heading';
import UsersForgotPasswordPageComponent from '../users/forgot-password';
import UsersResetPasswordPageComponent from '../users/reset-password';
import NotFoundPageComponent from '../text/not-found';
import ProfileLayoutComponent from '../../components/profile/layout';
import ProfileResultsAllergenGroupsPageComponent from '../profile/results/allergen-groups';
import ProfileResultsRecommendationsPageComponent from '../profile/results/recommendations';
import ProfileResultsAllergensPageComponent from '../profile/results/allergens';
import QuestionnaireFreePageComponent from '../questionnaire/free';
import ProfileMessagesPageComponent from '../profile/messages';
import ProfileSelectProductPageComponent from '../profile/select-product';
import ModalRemoteConsultation from '../../components/modal/remote-consultation';
import ResearchSpouseSignPageComponent from '../research/spouse-sign';
import QuestionnaireOrderedPageComponent from '../questionnaire/ordered';
import QuestionnaireRepeatPageComponent from '../questionnaire/repeat';
import UsersSelectPatientPageComponent from '../patients/select';
import UsersCreatePatientPageComponent from '../patients/create';
import QuestionnaireLoginPageComponent from '../questionnaire/login';
import QuestionnaireRegisterPageComponent from '../questionnaire/register';
import UsersRegisterPageComponent from '../users/register';
import QuestionnaireFeedbackPageComponent from '../questionnaire/feedback';
import FeedbackSuccessPageComponent from '../questionnaire/success';
import QuestionnaireStartConsultationPageComponent from '../questionnaire/startConsultation';
import ConsultationSuccessPageComponent from '../questionnaire/successConsultation';
import QuestionnaireCalendarPageComponent from '../questionnaire/calendar';
import QuestionnaireCheckAllergyPageComponent from '../questionnaire/checkAllergy';
import QuestionnaireSubscribeAnswerPageComponent from '../questionnaire/subscribeAnswer';
import ImmunotherapyAgreementPageComponent from '../immunotherapy/agreement';
import ModalTreatmentProtocol from '../../components/modal/treatment-protocol';
import ModalTreatmentProtocolChild from '../../components/modal/treatment-protocol-child';
import QuestionnaireWeeklyTreatmentPageComponent from '../questionnaire/weeklyTreatment';
import TreatmentWeeklySuccessPageComponent from '../questionnaire/successTreatmentWeekly';
import QuestionnaireScreenerAsitPageComponent from '../questionnaire/screenerAsit';
import QuestionnaireAnamnesisPageComponent from '../questionnaire/anamnesis/anamnesis';
import ModalClinicTermsConditions from '../../components/modal/clinic-terms-conditions';
import ModalClinicPrivacyPolicy from '../../components/modal/clinic-privacy-policy';
import AnamnesisSubmitPageComponent from '../questionnaire/anamnesis/submitAnamnesis';
import AnamnesisUploadPageComponent from '../questionnaire/anamnesis/uploadFile';
import RedirectToResearchLanding from '../redirect';
import QuestionnaireSelectReviewPageComponent from '../questionnaire/select-review';
import LayoutFooterComponent from '../../components/layout/footer';
import ResultsRedirectComponent from '../profile/results-redirect';
import QuestionnaireSelectPresalePageComponent from '../questionnaire/select-presale';
import RepeatSuccessPageComponent from '../questionnaire/successRepeat';
import QuestionnaireShipmentPageComponent from '../questionnaire/shipment';
import ShopifyWelcomePageComponent from '../questionnaire/shopify-welcome';

const PageContainer = styled.div`
  min-height: calc(100vh - 140px);
`;

function LocalizedNavigate({ to }: { to: string }) {
  const { locale, region } = useSite();

  return <Navigate to={getLocalizedPath(to, locale, region)} />;
}

export default function Root() {
  const { user, loading } = useAuth();

  const isLoggedIn = !loading && !!user;

  const element = useRoutes([
    ...createLocalizedRoutes(
      '/',
      isLoggedIn ? <LocalizedNavigate to="/profile/dashboard" /> : <UsersLoginPageComponent />,
    ),
    ...createLocalizedRoutes(
      '/register/:hash',
      isLoggedIn ? <LocalizedNavigate to="/profile/dashboard" /> : <UsersRegisterPageComponent />,
    ),
    ...createLocalizedRoutes('/questionnaire/login', <QuestionnaireLoginPageComponent />),
    ...createLocalizedRoutes('/questionnaire/register', <QuestionnaireRegisterPageComponent />),
    ...createLocalizedRoutes('/questionnaire/shopify/:shopifyOrderId', <ShopifyWelcomePageComponent />),
    ...createLocalizedRoutes('/questionnaire/shopify-welcome/:orderHash', <ShopifyWelcomePageComponent />),
    ...createLocalizedRoutes(
      '/questionnaire/patient',
      isLoggedIn ? <UsersSelectPatientPageComponent /> : <QuestionnaireLoginPageComponent />,
    ),
    ...createLocalizedRoutes(
      '/questionnaire/patient/create',
      isLoggedIn ? <UsersCreatePatientPageComponent /> : <QuestionnaireLoginPageComponent />,
    ),
    ...createLocalizedRoutesWithChildren('/questionnaire/onboarding', <Outlet />, [
      {
        path: '',
        element: <QuestionnaireOnboardingPageComponent />,
      },
      {
        path: ':productId',
        element: <Outlet />,
        children: [
          {
            path: '',
            element: <QuestionnaireOnboardingPageComponent />,
          },
          {
            path: ':step',
            element: <QuestionnaireOnboardingPageComponent />,
          },
        ],
      },
    ]),
    ...createLocalizedRoutesWithChildren(
      '/questionnaire/repeat/:orderHash/:productId',
      <QuestionnaireRepeatPageComponent />,
      [
        {
          path: '',
          element: <QuestionnaireOnboardingPageComponent />,
        },
        {
          path: ':extraSurveyId',
          element: <QuestionnaireOnboardingPageComponent />,
        },
      ],
    ),
    ...createLocalizedRoutes('/questionnaire/repeat/success/:orderHash', <RepeatSuccessPageComponent />),
    {
      path: '/questionnaire/research',
      element: <RedirectToResearchLanding />,
      // element: <Outlet />,
      // children: [
      //   {
      //     path: '',
      //     element: <QuestionnaireResearchPageComponent />,
      //   },
      //   {
      //     path: ':productId',
      //     element: <Outlet />,
      //     children: [
      //       {
      //         path: '',
      //         element: <QuestionnaireResearchPageComponent />,
      //       },
      //       {
      //         path: ':step',
      //         element: <QuestionnaireResearchPageComponent />,
      //       },
      //     ],
      //   },
      // ],
    },
    {
      path: '/questionnaire/screener-asic',
      element: <Outlet />,
      children: [
        {
          path: '',
          element: <QuestionnaireScreenerAsitPageComponent />,
        },
        {
          path: ':step',
          element: <QuestionnaireScreenerAsitPageComponent />,
        },
      ],
    },
    {
      path: '/questionnaire/anamnesis/:patientHash',
      element: <Outlet />,
      children: [
        {
          path: '',
          element: <QuestionnaireAnamnesisPageComponent />,
        },
        {
          path: 'send',
          element: <AnamnesisSubmitPageComponent />,
        },
        {
          path: 'upload',
          element: <AnamnesisUploadPageComponent />,
        },
      ],
    },
    { path: '/questionnaire/ordered/:orderHash/:productId', element: <QuestionnaireOrderedPageComponent /> },

    // with di code
    { path: '/questionnaire/free', element: <QuestionnaireFreePageComponent /> },

    ...createLocalizedRoutesWithChildren('/questionnaire/feedback', <Outlet />, [
      {
        path: '',
        element: <QuestionnaireFeedbackPageComponent />,
      },
      {
        path: ':feedbackHash/:feedbackValue',
        element: <Outlet />,
        children: [
          {
            path: '',
            element: <QuestionnaireFeedbackPageComponent />,
          },
        ],
      },
    ]),
    ...createLocalizedRoutes('/questionnaire/success', <FeedbackSuccessPageComponent />),

    ...createLocalizedRoutes(
      '/questionnaire/start/consultation/:answerHash',
      <QuestionnaireStartConsultationPageComponent />,
    ),
    ...createLocalizedRoutesWithChildren('/questionnaire/calendar', <Outlet />, [
      {
        path: '',
        element: <QuestionnaireCalendarPageComponent />,
      },
      {
        path: ':answerHash',
        element: <Outlet />,
        children: [
          {
            path: '',
            element: <QuestionnaireCalendarPageComponent />,
          },
          {
            path: ':allergistId',
            element: <QuestionnaireCalendarPageComponent />,
          },
        ],
      },
    ]),
    ...createLocalizedRoutes('/questionnaire/consultation/success', <ConsultationSuccessPageComponent />),

    {
      path: '/questionnaire/checkAllergy',
      element: <Outlet />,
      children: [
        {
          path: '',
          element: <QuestionnaireCheckAllergyPageComponent />,
        },
      ],
    },
    { path: '/questionnaire/select-review/:hash/:patientId', element: <QuestionnaireSelectReviewPageComponent /> },

    { path: '/questionnaire/select-presale/:extraProductId', element: <QuestionnaireSelectPresalePageComponent /> },

    { path: '/questionnaire/subscribeAnswer/:hash', element: <QuestionnaireSubscribeAnswerPageComponent /> },

    { path: '/questionnaire/answer/:hash', element: <QuestionnaireAnswerPageComponent /> },

    { path: '/questionnaire/treatment-weekly/:patientHash', element: <QuestionnaireWeeklyTreatmentPageComponent /> },
    {
      path: '/questionnaire/treatment-weekly/success/:patientHash/:answerHash',
      element: <TreatmentWeeklySuccessPageComponent />,
    },

    ...createLocalizedRoutes('/payments/checkout/:hash', <PaymentsCheckoutPageComponent />),
    ...createLocalizedRoutes('/payments/checkout/:hash/:patientId', <PaymentsCheckoutPageComponent />),
    ...createLocalizedRoutes(
      '/payments/checkout/:hash/:patientId/:treatmentAgreement',
      <PaymentsCheckoutPageComponent />,
    ),
    ...createLocalizedRoutes('/payments/success/:hash', <PaymentsSuccessPageComponent />),
    ...createLocalizedRoutes('/research/success/:hash/:productId', <PaymentsSuccessPageComponent />),
    ...createLocalizedRoutes('/payments/cancel/:hash', <PaymentsCancelPageComponent />),
    {
      path: '/research/sign/:hash',
      element: <PaymentsCheckoutPageComponent />,
    },
    {
      path: '/research/spouse/sign/:signToken/:signerToken/:orderHash',
      element: <ResearchSpouseSignPageComponent />,
    },
    ...createLocalizedRoutes('/privacy-policy', <TextPageComponent type="privacy" />),
    ...createLocalizedRoutes('/terms-conditions', <TextPageComponent type="terms" />),
    ...createLocalizedRoutes('/forgot-password', <UsersForgotPasswordPageComponent />),
    ...createLocalizedRoutes('/reset-password/:token', <UsersResetPasswordPageComponent />),

    ...createLocalizedRoutesWithChildren('/results/preview/:hash', <ProfileResultsPageComponent isPreview />, [
      {
        path: '',
        element: <ProfileResultsAllergenGroupsPageComponent />,
      },
      {
        path: 'recommendations',
        element: <ProfileResultsRecommendationsPageComponent />,
      },
      {
        path: 'allergens',
        element: <ProfileResultsAllergensPageComponent />,
      },
    ]),
    ...createLocalizedRoutesWithChildren(
      '/profile',
      isLoggedIn ? <ProfileLayoutComponent /> : <LocalizedNavigate to="/" />,
      [
        {
          path: 'dashboard',
          element: <ProfileDashboardPageComponent />,
        },
        {
          path: 'messages',
          element: <ProfileMessagesPageComponent />,
        },
        {
          path: 'select-product/:hash',
          element: <Outlet />,
          children: [
            {
              path: '',
              element: <ProfileSelectProductPageComponent />,
            },
            {
              path: ':productId',
              element: <ProfileSelectProductPageComponent />,
            },
          ],
        },
        {
          path: 'results/:hash',
          element: <ProfileResultsPageComponent />,
          children: [
            {
              path: '',
              element: <ProfileResultsAllergenGroupsPageComponent />,
            },
            {
              path: 'recommendations',
              element: <ProfileResultsRecommendationsPageComponent />,
            },
            {
              path: 'allergens',
              element: <ProfileResultsAllergensPageComponent />,
            },
          ],
        },
        {
          path: 'blood-registration/:hash',
          element: <ProfileBloodRegistrationPageComponent />,
        },
      ],
    ),
    {
      path: 'purchase/treatment-plan/:patientHash/:productId',
      element: isLoggedIn ? <ImmunotherapyAgreementPageComponent /> : <UsersLoginPageComponent />,
    },
    ...createLocalizedRoutes('results/redirect/:orderId/:token', <ResultsRedirectComponent />),
    ...createLocalizedRoutes(
      '/questionnaire/shipment',
      isLoggedIn ? <QuestionnaireShipmentPageComponent /> : <LocalizedNavigate to="/questionnaire/register" />,
    ),
  ]);

  return (
    <>
      <PageContainer>
        <LayoutHeadingComponent />

        {loading && <CommonAnimatedLoader />}
        {!loading && (element || <NotFoundPageComponent />)}

        <ModalPrivacyPolicy />
        <ModalTermsConditions />
        <ModalRemoteConsultation />
        <ModalTreatmentProtocol />
        <ModalTreatmentProtocolChild />
        <ModalClinicTermsConditions />
        <ModalClinicPrivacyPolicy />

        {parameters().isDevelopment && <ResponsiveUtility />}
      </PageContainer>
      <LayoutFooterComponent />
    </>
  );
}
